/**
 * 印表機缺紙狀態 — Flow 4.1 / HW-004。
 *
 * 與 apps/pwa/src/lib/kiosk-printer.ts 同款設計（共享 storage key 與 event
 * 名稱方便日後硬體 SDK 整合）；獨立檔避免跨 app 依賴。
 *
 * 啟用方式（任一即可）：
 *   - URL `?printer-out=1` 開啟 / `?printer-out=0` 關閉（皆 persist）
 *   - localStorage `boat.pwa.kiosk_printer_out=on`
 *   - 硬體 SDK 呼叫 `setPrinterPaperOut(true/false)` 觸發事件
 *
 * 缺紙時：橫幅顯示 HW-004 + 紙本 QR 選項 disabled + 強制走 Email / LINE。
 * 補紙後事件廣播自動恢復實體出票。
 */

const STORAGE_KEY = 'boat.pwa.kiosk_printer_out';

/** HW-004 文案 — 與 packages/error-codes registry 同步之 UI 子集。 */
export const HW_004 = {
  code: 'HW-004',
  message: {
    'zh-TW': '印表機缺紙；已自動切換為「僅數位票」模式',
    en: 'Printer out of paper; switched to digital-only ticket mode',
    ja: 'プリンター用紙切れ；デジタル専用券モードに切り替え',
  },
  suggestion: {
    'zh-TW': '請補紙，或改用 Email / LINE 收票',
    en: 'Refill paper, or receive via Email / LINE',
    ja: '用紙を補充するか、Email / LINEで受け取ってください',
  },
} as const;

export const PRINTER_STATUS_EVENT = 'boat:kiosk-printer-status';

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

/** 當前是否缺紙（URL > storage）。URL `?printer-out=0` 會清除 storage。 */
export function isPrinterPaperOut(): boolean {
  if (!hasWindow()) return false;
  const raw = new URL(window.location.href).searchParams.get('printer-out');
  if (raw === '1') {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'on');
    } catch {
      /* ignore — localStorage may be blocked */
    }
    return true;
  }
  if (raw === '0') {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return false;
  }
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

/** 由硬體 SDK / e2e 呼叫，設狀態並廣播。 */
export function setPrinterPaperOut(out: boolean): void {
  if (!hasWindow()) return;
  try {
    if (out) {
      window.localStorage.setItem(STORAGE_KEY, 'on');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(PRINTER_STATUS_EVENT, { detail: { out } }));
}
